import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import articles from '../content/manifest.json';

const InsightsArticle = () => {
  const { slug } = useParams();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (!article) {
      setLoading(false);
      return;
    }

    fetch(`/content/insights/${slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug, article]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  };

  if (!article) {
    return (
      <div>
        <Navigation />
        <section className="hero-section bg-gradient-to-br from-gray-900 via-onyx-900 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl text-white mb-4">Article Not Found</h1>
            <Link to="/insights" className="text-white underline">
              Back to Insights
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navigation />

      {/* Hero Section */}
      <section className="hero-section bg-gradient-to-br from-gray-900 via-onyx-900 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-rose-300 font-semibold mb-4">
              {formatDate(article.date)} &middot; {article.readingTime} min read
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl text-white mb-6">
              {article.title}
            </h1>
            {article.author && (
              <p className="text-lg text-gray-300">
                By {article.author}
                {article.authorTitle && `, ${article.authorTitle}`}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Article Hero Image */}
      {article.image && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <img
            src={article.image}
            alt={article.title}
            className="w-full rounded-lg shadow-xl"
          />
        </div>
      )}

      {/* Article Body */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-gray-500 py-12">Loading...</div>
          ) : (
            <div className="article-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
            </div>
          )}

          <div className="mt-16 pt-8 border-t border-gray-200">
            <Link
              to="/insights"
              className="text-accent font-semibold hover:text-accent-700 transition-colors duration-200"
            >
              &larr; Back to Insights
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsightsArticle;
